import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Steps, Button, Form, Input, Select, Checkbox, Radio, DatePicker, TimePicker, InputNumber, Rate, Slider, Switch, Card, Alert, Layout, Row, Col, Modal, Table } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, LogoutOutlined, UserOutlined, HistoryOutlined } from '@ant-design/icons';
import authService from '../services/authService';
import { getSiteVisitFormsById, getLeadByPhoneNumber, getLeadById, upsertLead, getFieldChoices } from '../services/apiService';
import FullPageLoader from '../components/FullPageLoader';
import AppHeader from '../components/Header';
import dayjs from 'dayjs';
const pageStyles = `
.ant-btn{
height: 45px;
}
 .back-btn {
    background: #5568d3 !important;
    border-color: #5568d3 !important;
    color: #fff !important;
  }

  .back-btn:hover {
    background: #5568d3 !important;
    border-color: #5568d3 !important;
    color: #fff !important;
  }

  .history-btn {
    border-color: #5568d3 !important;
    color: #5568d3 !important;
  }

  .history-btn:hover {
    background: #5568d3 !important;
    border-color: #5568d3 !important;
    color: #fff !important;
  }
    .ant-input{
    height: 45px;
    border: 1px solid #d9d9d9;
    margin-top: 10px;
    }
    .ant-select{
    height: 45px;
    border: 1px solid #d9d9d9;
    margin-top: 10px;
    }
    .ant-picker{
    height: 45px;
    border: 1px solid #d9d9d9;
    margin-top: 10px;
    }
    .ant-input-number{
    margin-top: 10px;
    }
    .ant-checkbox-wrapper{
    margin-top: 10px;
    }
    .ant-radio-group{
    margin-top: 10px;
    }
    .ant-switch{
    margin-top: 10px;
    }
    .ant-rate{
    margin-top: 10px;
    }
    .ant-slider{
    margin-top: 10px;
    }
    .ant-form-item-label{
    font-weight: 600;
    font-size: 14px;
    position: relative;
    padding-bottom: 8px;
    margin-bottom: 12px;
    }
    .ant-form-item-label::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, #5568d3, #e0e0e0);
    border-radius: 1px;
    }
`;

const { Content } = Layout;

// Component to handle Select fields with kernel params
const KernelParamsSelect = ({ field, fieldKey, formValues, setFormValues, loadFieldChoices, formatOptions, mode, form }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      // First, check if field has values/options defined
      const fieldOptions = field.values || field.options || [];
      
      if (fieldOptions.length > 0) {
        // Use field's own options if available
        //console.log(`Using field options for ${field.name}:`, fieldOptions.length, 'options');
        setOptions(formatOptions(fieldOptions));
      } else {
        // Otherwise, try to load from kernel params
        //console.log(`Loading choices from kernel params for field: ${field.name}`);
        setLoading(true);
        try {
          const choices = await loadFieldChoices(field.name);
          if (choices && choices.length > 0) {
            //console.log(`Loaded ${choices.length} choices from kernel params for ${field.name}`);
            setOptions(formatOptions(choices));
          } else {
            console.warn(`No choices found in kernel params for ${field.name}`);
          }
        } catch (error) {
          console.error(`Error loading choices for ${field.name}:`, error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOptions();
  }, [field.name, field.values, field.options]);

  const selectMode = mode === 'multiple' ? 'multiple' : (mode === 'tags' ? 'tags' : undefined);

  return (
    <Select
      mode={selectMode}
      showSearch
      allowClear
      loading={loading}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      placeholder={field.placeholder || `Select ${field.label || field.name}`}
      options={options}
      onChange={(val) => {
        setFormValues({ ...formValues, [fieldKey]: val });
        form.setFieldValue(fieldKey, val);
      }}
      value={formValues[fieldKey]}
      notFoundContent={loading ? 'Loading...' : 'No options available'}
    />
  );
};

const FormWizard = () => {
  const { companyId, formId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [user, setUser] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [mobileNumber, setMobileNumber] = useState('');
  const [leadData, setLeadData] = useState(null);
  const [isLeadCheckStep, setIsLeadCheckStep] = useState(true);
  const [fieldChoicesCache, setFieldChoicesCache] = useState({});
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  const token = authService.getToken();

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authService.getUserDetails();
        setUser(userData);
        
        // Set GRE Users field with current logged-in user
        if (userData && formData && formData.fields) {
          const greField = formData.fields.find(f => f.label === 'GRE Users');
          if (greField) {
            const fieldKey = greField.id || greField.name;
            setFormValues(prev => ({
              ...prev,
              [fieldKey]: userData.email || userData.user_id || userData.id
            }));
            form.setFieldValue(fieldKey, userData.email || userData.user_id || userData.id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user details', err);
      }
    };
    fetchUser();
  }, [formData, form]);

  // Fetch form data using apiService
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getSiteVisitFormsById(companyId, formId, token);
        
        if (mounted) {
          setFormData(data);
          //console.log('Form data fetched:', data);
        }
      } catch (err) {
        console.error('Failed to fetch form data', err);
        if (mounted) {
          setError(err?.message || 'Failed to load form');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [companyId, formId, token]);

  // Function to prepare site visit history data
  const prepareSiteVisitHistoryData = () => {
    if (!formData?.meta?.history_field_mappings || !leadData) {
      return [];
    }

    const historyMappings = formData.meta.history_field_mappings;
    const leadFields = leadData.lead_fields || {};
    
    return historyMappings.map((mapping, index) => {
      // Get values from lead_fields using the mapping
      const dateValue = leadFields[mapping.dateField] || '';
      const projectUnitValue = leadFields[mapping.projectUnitField] || '';
      const budgetValue = leadFields[mapping.budgetField] || '';
      const channelPartnerValue = leadFields[mapping.channelPartnerField] || '';

      return {
        key: mapping.id || index,
        date: Array.isArray(dateValue) ? dateValue[0] : dateValue,
        projectUnit: Array.isArray(projectUnitValue) ? projectUnitValue[0] : projectUnitValue,
        budget: Array.isArray(budgetValue) ? budgetValue[0] : budgetValue,
        channelPartner: Array.isArray(channelPartnerValue) ? channelPartnerValue[0] : channelPartnerValue,
      };
    }).filter(item => item.date || item.projectUnit || item.budget || item.channelPartner); // Filter out empty rows
  };

  // Function to show site visit history modal
  const showSiteVisitHistory = () => {
    const historyData = prepareSiteVisitHistoryData();
    setHistoryData(historyData);
    setHistoryModalVisible(true);
  };

  // Function to map lead data to form fields
  const mapLeadDataToFormFields = (leadInfo, formFields) => {
    const mappedValues = {};

    //console.log("Mapped data called", leadInfo, formFields);
    
    if (!leadInfo || !formFields) return mappedValues;
    
    // Helper function to find field in both main fields and sub_fields
    const findFieldByName = (fieldName) => {
      // First check main fields
      let field = formFields.find(f => f.name === fieldName);
      if (field) return field;
      
      // Then check sub_fields of all fields
      for (const mainField of formFields) {
        if (mainField.sub_fields && Array.isArray(mainField.sub_fields)) {
          field = mainField.sub_fields.find(sf => sf.name === fieldName);
          if (field) return field;
        }
      }
      return null;
    };
    
    // Helper function to map a value based on field type
    const mapFieldValue = (field, value) => {
      if (!value) return value;
      
      // Handle address objects - extract address_line value
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Check if this looks like an address object (has address_line property)
        if (value.address_line) {
          return value.address_line;
        }
        // If it's an object but not an address, return as is for other object types
      }
      
      // Handle address arrays - extract address_line from last address
      if (Array.isArray(value)) {
        // Check if this is an array of address objects
        if (value.length > 0 && typeof value[0] === 'object' && value[0]?.address_line) {
          const lastAddress = value[value.length - 1];
          return lastAddress.address_line;
        }
        
        // Handle other array values (take first element if single item array)
        if (value.length === 1) {
          value = value[0];
        } else if (value.length === 0) {
          return null;
        }
      }
      
      // Handle date fields
      if (field.type === 'date' || field.type === 'datetime') {
        if (value && typeof value === 'string') {
          // Extract date part from ISO string
          value = value.split('T')[0];
        }
      }
      
      return value;
    };
    
    // Map basic fields (top-level properties)
    const basicFields = ['name', 'email', 'phone', 'state', 'lead_id'];
    basicFields.forEach(fieldName => {
      if (leadInfo[fieldName]) {
        const formField = findFieldByName(fieldName);
        if (formField) {
          const fieldKey = formField.id || formField.name;
          mappedValues[fieldKey] = mapFieldValue(formField, leadInfo[fieldName]);
        }
      }
    });
    
    // Handle phones array
    if (leadInfo.phones && Array.isArray(leadInfo.phones) && leadInfo.phones.length > 0) {
      const phoneValue = leadInfo.phones[0].phone;
      const phoneField = findFieldByName('phone');
      if (phoneField && phoneValue) {
        const fieldKey = phoneField.id || phoneField.name;
        mappedValues[fieldKey] = phoneValue;
      }
    }
    
    // Handle emails array - use last value
    if (leadInfo.emails && Array.isArray(leadInfo.emails) && leadInfo.emails.length > 0) {
      const emailValue = leadInfo.emails[leadInfo.emails.length - 1].email;
      const emailField = findFieldByName('email');
      if (emailField && emailValue) {
        const fieldKey = emailField.id || emailField.name;
        mappedValues[fieldKey] = emailValue;
      }
    }
    
    // Handle source_tags array
    if (leadInfo.source_tags && Array.isArray(leadInfo.source_tags)) {
      const sourceField = findFieldByName('source_tags');
      if (sourceField) {
        const fieldKey = sourceField.id || sourceField.name;
        mappedValues[fieldKey] = leadInfo.source_tags;
      }
    }
    
    
    // Handle assigned_to object for Sales Manager and Sourcing Manager
    if (leadInfo.assigned_to) {
      // Map to Sales Manager field
      const salesManagerField = formFields.find(f => 
        f.name === 'sales_managers' || 
        f.label === 'Sales Manager' || 
        f.name === 'assigned_to'
      );
      if (salesManagerField) {
        const fieldKey = salesManagerField.name;
        //console.log('fieldKey',fieldKey);
        mappedValues[fieldKey] = leadInfo.assigned_to.name;
      }
  //  console.log('leadInfo.assigned_to',leadInfo.assigned_to.name);

    }
    
    // Map lead_fields (additional custom fields including sub-fields)
    if (leadInfo.lead_fields && typeof leadInfo.lead_fields === 'object') {
      Object.keys(leadInfo.lead_fields).forEach(leadFieldKey => {
        // Find matching form field by name (including sub-fields)
        const formField = findFieldByName(leadFieldKey);
        if (formField) {
          const fieldKey = formField.id || formField.name;
          const value = leadInfo.lead_fields[leadFieldKey];
          mappedValues[fieldKey] = mapFieldValue(formField, value);
        }
      });
    }
    
    //console.log('Mapped lead data to form fields:', mappedValues);
    return mappedValues;
  };

  const handleLeadCheck = async () => {
    if (!mobileNumber) {
      alert('Please enter a mobile number to continue.');
      return;
    }

    setSubmitting(true);
    try {
      // Search for lead by phone number
      const searchResponse = await getLeadByPhoneNumber(companyId, mobileNumber);
      //console.log('Lead search response:', searchResponse);

      const initialValues = {
        phone: mobileNumber
      };

      // Check if lead was found in search results
      if (searchResponse.data && searchResponse.data.length > 0) {
        const searchResult = searchResponse.data[0];
        const leadId = searchResult.lead_id;
        
        //console.log('Lead found with ID:', leadId);
        
        // Fetch full lead details using lead_id
        if (leadId) {
          const leadDetailsResponse = await getLeadById(companyId, leadId);
          //console.log('Full lead details:', leadDetailsResponse);
          
          if (leadDetailsResponse) {
            const leadInfo = leadDetailsResponse;
            
            // Create userAssignedObject from assigned_to
            let userAssignedObject = null;
            if (leadInfo.assigned_to) {
              userAssignedObject = {
                user_id: leadInfo.assigned_to.id,
                id: leadInfo.assigned_to.id,
                name: leadInfo.assigned_to.name,
                email_id: leadInfo.assigned_to.user_phone || '',
                phone_number: leadInfo.assigned_to.user_phone || '',
                tracking_number: leadInfo.assigned_to.tracking_number || ''
              };
              //console.log('Created userAssignedObject:', userAssignedObject);
            }
            
            // Use mapping function to extract and map all lead fields
            const mappedLeadData = mapLeadDataToFormFields(leadInfo, formData.fields);
            
            // If userAssignedObject exists, use it for manager fields
            if (userAssignedObject) {
              // Map to Sales Manager field
              const salesManagerField = formData.fields.find(f => 
                f.name === 'sales_managers' || 
                f.label === 'Sales Manager'
              );
              if (salesManagerField) {
                const fieldKey = salesManagerField.name;
                mappedLeadData[fieldKey] = userAssignedObject.name || userAssignedObject.id;
              }
            
            }
            
            // Merge mapped lead data with initial values
            Object.assign(initialValues, mappedLeadData);
            
            // Store lead data for reference
            setLeadData(leadInfo);
          }
        }
      } else {
        //console.log('No existing lead found for this phone number');
      }
      
      // Set GRE Users field with current logged-in user
      if (user && formData && formData.fields) {
        const greField = formData.fields.find(f => f.label === 'GRE Users');
        if (greField) {
          const fieldKey = greField.id || greField.name;
          initialValues[fieldKey] = user.email || user.user_id || user.id;
        }
      }
      
      // Convert date strings to dayjs objects for DatePicker fields
      const formValuesForAntd = {};
      Object.keys(initialValues).forEach(key => {
        const field = formData.fields.find(f => (f.id || f.name) === key);
        const value = initialValues[key];
        
        if (field && value) {
          if (field.type === 'date' || field.type === 'datetime') {
            // Convert string dates to dayjs objects for Ant Design Form
            formValuesForAntd[key] = dayjs(value);
          } else if (field.type === 'time') {
            formValuesForAntd[key] = dayjs(value, 'HH:mm:ss');
          } else {
            formValuesForAntd[key] = value;
          }
        } else {
          formValuesForAntd[key] = value;
        }
      });
      
      setFormValues(initialValues);
      form.setFieldsValue(formValuesForAntd);
      
    } catch (error) {
      console.error("Error checking lead:", error);
    } finally {
      setIsLeadCheckStep(false);
      setSubmitting(false);
    }
  };

  // Load field choices from kernel params
  const loadFieldChoices = async (fieldName) => {
    // Check if already cached
    if (fieldChoicesCache[fieldName]) {
      return fieldChoicesCache[fieldName];
    }

    try {
      const choices = await getFieldChoices(companyId, fieldName);
      
      // Cache the choices
      setFieldChoicesCache(prev => ({
        ...prev,
        [fieldName]: choices
      }));
      
      return choices;
    } catch (error) {
      console.error(`Failed to load choices for field ${fieldName}:`, error);
      return [];
    }
  };

  // Convert options array to Select options format
  const formatOptions = (options, displayField = 'name', valueField = 'id') => {
    if (!Array.isArray(options)) return [];

    return options.map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        const display = opt.name || opt.label || opt.email_id || opt.phone_number || JSON.stringify(opt);
        const value = opt.id || opt.user_id || opt.email_id || opt.phone_number || display;

        return {
          label: display,
          value: value,
          data: opt
        };
      }
      return {
        label: opt,
        value: opt
      };
    });
  };

  // Sort fields by order property
  const sortFieldsByOrder = (fieldsArray) => {
    return fieldsArray.sort((a, b) => {
      const orderA = a.order !== undefined ? parseInt(a.order) : Infinity;
      const orderB = b.order !== undefined ? parseInt(b.order) : Infinity;
      return orderA - orderB;
    });
  };

  // Check if parent field has value to show sub_fields
  const shouldShowSubFields = (field) => {
    const fieldKey = field.id || field.name;
    const fieldValue = formValues[fieldKey];
    
    // Check if field has a value
    if (field.type === 'checkbox' || field.type === 'switch' || field.type === 'toggle') {
      return fieldValue === true;
    }
    
    // For select/dropdown/multiselect/radio, check if value exists
    if (Array.isArray(fieldValue)) {
      return fieldValue.length > 0;
    }
    
    return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
  };

  // Filter sub-fields based on parent field's selected value
  const getRelevantSubFields = (field) => {
    const fieldKey = field.id || field.name;
    const fieldValue = formValues[fieldKey];
    
    if (!field.sub_fields || !Array.isArray(field.sub_fields)) {
      return [];
    }

    // If no value selected, return empty array
    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
      return [];
    }

    // Special filtering only for "Sub Source" field
    const fieldLabel = (field.label || '').toLowerCase();
    const isSubSourceField = fieldLabel === 'sub source';

    // For non-Sub Source fields, return all sub-fields
    if (!isSubSourceField) {
      return field.sub_fields;
    }

    // Get the selected value(s)
    const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    
    // Filter sub-fields based on matching criteria (only for Sub Source)
    // Check if sub-field name or label contains the selected value
    const relevantSubFields = field.sub_fields.filter(subField => {
      // Check each selected value
      return selectedValues.some(selectedValue => {
        const selectedStr = String(selectedValue).toLowerCase();
        const subFieldName = (subField.name || '').toLowerCase();
        const subFieldLabel = (subField.label || '').toLowerCase();
        
        // Match if sub-field name or label contains the selected value
        // or if the selected value is in the sub-field's name
        return subFieldName.includes(selectedStr) || 
               selectedStr.includes(subFieldName.replace(/_/g, ' ')) ||
               subFieldLabel.includes(selectedStr);
      });
    });

    // Return only matched sub-fields, empty array if nothing matches
    return relevantSubFields;
  };

  // Render field based on type
  const renderField = (field, isSubField = false) => {
    const fieldKey = field.id || field.name;
    const fieldValue = formValues[fieldKey];
    const hasSubFields = field.sub_fields && Array.isArray(field.sub_fields) && field.sub_fields.length > 0;

    const commonProps = {
      name: fieldKey,
      label: field.label || field.name,
      required: field.required || false,
      readOnly: field.label === 'GRE Users' ? true : false,
      value: fieldValue,
      disabled: field.label === 'GRE Users' ? true : false,
      rules: field.required ? [{ required: true, message: `${field.label || field.name} is required` }] : []
    };

    let fieldComponent;

    switch (field.type) {
      case 'text':
      case 'email':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Input
              type={field.type}
              placeholder={field.placeholder || `Enter ${field.label || field.name}`}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormValues({ ...formValues, [fieldKey]: newValue });
                form.setFieldValue(fieldKey, newValue);
              }}
            />
          </Form.Item>
        );
        break;
      case 'phone':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Input
              readOnly={true}
              type={field.type}
              placeholder={field.placeholder || `Enter ${field.label || field.name}`}
              onChange={(e) => setFormValues({ ...formValues, [fieldKey]: e.target.value })}
            />
          </Form.Item>
        );
        break;
      case 'url':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Input
              type={field.type}
              placeholder={field.placeholder || `Enter ${field.label || field.name}`}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormValues({ ...formValues, [fieldKey]: newValue });
                form.setFieldValue(fieldKey, newValue);
              }}
            />
          </Form.Item>
        );
        break;

      case 'textarea':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Input.TextArea
              rows={field.rows || 4}
              placeholder={field.placeholder || `Enter ${field.label || field.name}`}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormValues({ ...formValues, [fieldKey]: newValue });
                form.setFieldValue(fieldKey, newValue);
              }}
            />
          </Form.Item>
        );
        break;

      case 'number':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder={field.placeholder || 'Enter number'}
              onChange={(val) => {
                setFormValues({ ...formValues, [fieldKey]: val });
                form.setFieldValue(fieldKey, val);
              }}
            />
          </Form.Item>
        );
        break;

      case 'select':
      case 'dropdown':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <KernelParamsSelect
              field={field}
              fieldKey={fieldKey}
              formValues={formValues}
              setFormValues={setFormValues}
              loadFieldChoices={loadFieldChoices}
              formatOptions={formatOptions}
              mode="single"
              form={form}
            />
          </Form.Item>
        );
        break;

      case 'multidropdown':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <KernelParamsSelect
              field={field}
              fieldKey={fieldKey}
              formValues={formValues}
              setFormValues={setFormValues}
              loadFieldChoices={loadFieldChoices}
              formatOptions={formatOptions}
              mode="multiple"
              form={form}
            />
          </Form.Item>
        );
        break;

      case 'tags':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <KernelParamsSelect
              field={field}
              fieldKey={fieldKey}
              formValues={formValues}
              setFormValues={setFormValues}
              loadFieldChoices={loadFieldChoices}
              formatOptions={formatOptions}
              mode="tags"
              form={form}
            />
          </Form.Item>
        );
        break;

      case 'object':
        const isGreField = field.label === 'GRE Users';
        const greValue = isGreField && user ? (user.email || user.user_id || user.id) : fieldValue;
        
        fieldComponent = (
          <Form.Item {...commonProps} initialValue={isGreField && user ? greValue : undefined}>
            <Select
              showSearch
              allowClear={!isGreField}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder={field.placeholder || `Select ${field.label || field.name}`}
              options={formatOptions(field.options)}
              onChange={(val) => {
                setFormValues({ ...formValues, [fieldKey]: val });
                form.setFieldValue(fieldKey, val);
              }}
              optionLabelProp="label"
              readOnly={isGreField}
              disabled={isGreField}
              value={greValue}
            />
          </Form.Item>
        );
        break;

      case 'checkbox':
        fieldComponent = (
          <Form.Item {...commonProps} valuePropName="checked">
            <Checkbox
              onChange={(e) => {
                const checked = e.target.checked;
                setFormValues({ ...formValues, [fieldKey]: checked });
                form.setFieldValue(fieldKey, checked);
              }}
            >
              {field.label || field.name}
            </Checkbox>
          </Form.Item>
        );
        break;

      case 'radio':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Radio.Group
              options={formatOptions(field.options)}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormValues({ ...formValues, [fieldKey]: newValue });
                form.setFieldValue(fieldKey, newValue);
              }}
            />
          </Form.Item>
        );
        break;

      case 'date':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder={field.placeholder || 'Select date'}
              value={fieldValue ? dayjs(fieldValue) : null}
              onChange={(date) => {
                const dateStr = date?.format('YYYY-MM-DD');
                setFormValues({ ...formValues, [fieldKey]: dateStr });
                form.setFieldValue(fieldKey, date);
              }}
            />
          </Form.Item>
        );
        break;

      case 'time':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <TimePicker
              style={{ width: '100%' }}
              placeholder={field.placeholder || 'Select time'}
              value={fieldValue ? dayjs(fieldValue, 'HH:mm:ss') : null}
              onChange={(time) => {
                const timeStr = time?.format('HH:mm:ss');
                setFormValues({ ...formValues, [fieldKey]: timeStr });
                form.setFieldValue(fieldKey, time);
              }}
            />
          </Form.Item>
        );
        break;

      case 'datetime':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder={field.placeholder || 'Select date and time'}
              value={fieldValue ? dayjs(fieldValue) : null}
              onChange={(date) => {
                const dateTimeStr = date?.format('YYYY-MM-DD HH:mm:ss');
                setFormValues({ ...formValues, [fieldKey]: dateTimeStr });
                form.setFieldValue(fieldKey, date);
              }}
            />
          </Form.Item>
        );
        break;

      case 'toggle':
      case 'switch':
        fieldComponent = (
          <Form.Item {...commonProps} valuePropName="checked">
            <Switch
              onChange={(checked) => {
                setFormValues({ ...formValues, [fieldKey]: checked });
                form.setFieldValue(fieldKey, checked);
              }}
            />
          </Form.Item>
        );
        break;

      case 'rating':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Rate
              onChange={(val) => {
                setFormValues({ ...formValues, [fieldKey]: val });
                form.setFieldValue(fieldKey, val);
              }}
            />
          </Form.Item>
        );
        break;

      case 'slider':
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Slider
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
              onChange={(val) => {
                setFormValues({ ...formValues, [fieldKey]: val });
                form.setFieldValue(fieldKey, val);
              }}
            />
          </Form.Item>
        );
        break;

      default:
        fieldComponent = (
          <Form.Item {...commonProps}>
            <Input
              placeholder={field.placeholder || `Enter ${field.label || field.name}`}
              onChange={(e) => {
                const newValue = e.target.value;
                setFormValues({ ...formValues, [fieldKey]: newValue });
                form.setFieldValue(fieldKey, newValue);
              }}
            />
          </Form.Item>
        );
    }

    // Return just the field component, sub_fields will be handled separately
    return fieldComponent;
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      //console.log('Validation failed');
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    //console.log('handleSubmit called');
    //console.log('Current formValues:', formValues);
    
    try {
      // Validate all fields
      const values = await form.validateFields();
      //console.log('Form validated successfully:', values);
      
      setSubmitting(true);
      
      // Merge form values with validated values
      const allValues = { ...formValues, ...values };
      
      // Ensure GRE Users field is set with current user
      if (user && formData && formData.fields) {
        const greField = formData.fields.find(f => f.label === 'GRE Users');
        if (greField) {
          const fieldKey = greField.id || greField.name;
          allValues[fieldKey] = user.email || user.user_id || user.id;
        }
      }
      
      // Prepare data for API submission
      const submitData = {
        name: allValues.name,
        phone: allValues.phone || mobileNumber,
        email: allValues.email,
        state: 'site_visit_done'
      };

      // Add all other form fields
      Object.keys(allValues).forEach(key => {
        if (key !== 'name' && key !== 'phone' && key !== 'email') {
          submitData[key] = allValues[key];
        }
      });

      // Add current date for visit date if not already set
      const currentDate = new Date().toISOString().split('T')[0];
      if (!submitData['visit_details_bab.date_598']) {
        submitData['visit_details_bab.date_598'] = currentDate;
      }

      // Add source_tags if not from existing lead
      if (!submitData.source_tags || submitData.source_tags.length === 0) {
        submitData.source_tags = ['Site Visit Form'];
      }

      //console.log('Submitting form data:', submitData);
      
      // Call API to upsert lead
      const response = await upsertLead(companyId, submitData);
      //console.log('API Response:', response);
      
      // Navigate to thank you page
      navigate(`/companies/${companyId}/site_visit_forms/${formId}/thank-you`);
    } catch (err) {
      console.error('Submission failed', err);
      
      // Check if it's a validation error
      if (err.errorFields) {
        console.error('Validation errors:', err.errorFields);
        alert('Please fill in all required fields correctly.');
      } else {
        alert(err?.response?.data?.message || err?.message || 'Failed to submit form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    setDrawerVisible(false);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true
    }
  ];

  if (loading) return <FullPageLoader message="Loading form..." />;

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: 20 }}
      />
    );
  }


  const formMeta = formData.meta || {};
  let fields = formData.fields || [];
  
  // Add default fields at the beginning
  const defaultFields = [
    
  ];
  
  // Combine default fields with API fields
  fields = [...defaultFields, ...fields];
  
  fields = sortFieldsByOrder(fields);
  
  const fieldsPerStep = 50;
  const totalSteps = Math.ceil(fields.length / fieldsPerStep) || 1;
  const currentFields = fields.slice(currentStep * fieldsPerStep, (currentStep + 1) * fieldsPerStep);

  // Group fields according to meta.layout configuration
  const groupFieldsIntoRows = (fieldsArray) => {
    // Check if layout configuration exists in meta
    const layoutConfig = formData?.meta?.layout;
    
    //console.log('Layout Config:', layoutConfig);
    //console.log('Fields Array:', fieldsArray);
    
    if (!layoutConfig || !layoutConfig.rows || layoutConfig.rows.length === 0) {
      // Fallback to default layout logic if no layout config
      const rows = [];
      const processedFieldNames = new Set();
      
      // Priority fields that should always be in the first row
      const priorityFieldNames = ['phone', 'name', 'email'];
      const firstRowFields = [];
      
      // Add priority fields to first row if they exist
      fieldsArray.forEach(field => {
        if (priorityFieldNames.includes(field.name)) {
          field._columnCount = 3; // 3 columns in first row
          firstRowFields.push(field);
          processedFieldNames.add(field.name);
        }
      });
      
      // Add first row if we have priority fields
      if (firstRowFields.length > 0) {
        rows.push(firstRowFields);
      }
      
      // Process remaining fields
      let currentRow = [];
      fieldsArray.forEach((field) => {
        // Skip if already processed as priority field
        if (processedFieldNames.has(field.name)) {
          return;
        }
        
        const layout = field.layout || 'quarter';
          
        if (layout === 'full') {
          if (currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
          }
          rows.push([field]);
        }
        else if (layout === 'half') {
          currentRow.push(field);
          if (currentRow.length === 2) {
            rows.push(currentRow);
            currentRow = [];
          }
        }
        else if (layout === 'quarter') {
          currentRow.push(field);
          if (currentRow.length === 4) {
            rows.push(currentRow);
            currentRow = [];
          }
        }
        else {
          currentRow.push(field);
          if (currentRow.length === 3) {
            rows.push(currentRow);
            currentRow = [];
          }
        }
      });

      if (currentRow.length > 0) {
        rows.push(currentRow);
      }

      return rows;
    }

    // Use layout configuration from meta
    const rows = [];
    const fieldMap = {};
    
    // Create a map of field names to field objects for quick lookup
    fieldsArray.forEach(field => {
      fieldMap[field.name] = field;
    });

    // Priority fields that should always be in the first row
    const priorityFieldNames = ['phone', 'name', 'email'];
    const firstRowFields = [];
    const processedFieldNames = new Set();

    // Add priority fields to first row if they exist
    priorityFieldNames.forEach(fieldName => {
      const field = fieldMap[fieldName];
      if (field) {
        field._columnCount = 3; // 3 columns in first row
        firstRowFields.push(field);
        processedFieldNames.add(fieldName);
      }
    });

    // Add first row if we have priority fields
    if (firstRowFields.length > 0) {
      rows.push(firstRowFields);
      //console.log('First row (priority fields):', firstRowFields);
    }

    // Sort layout rows by order
    const sortedLayoutRows = [...layoutConfig.rows].sort((a, b) => (a.order || 0) - (b.order || 0));

    // Build rows according to layout configuration (excluding priority fields)
    sortedLayoutRows.forEach(layoutRow => {
      const rowFields = [];
      
      if (layoutRow.fields && Array.isArray(layoutRow.fields)) {
        layoutRow.fields.forEach(fieldName => {
          // Skip if already processed as priority field
          if (processedFieldNames.has(fieldName)) {
            return;
          }
          
          const field = fieldMap[fieldName];
          if (field) {
            // Add column count info to field for responsive layout
            field._columnCount = layoutRow.columnCount || layoutRow.fields.length;
            rowFields.push(field);
            processedFieldNames.add(fieldName);
          }
        });
      }
      
      if (rowFields.length > 0) {
        rows.push(rowFields);
      }
    });

    //console.log('Generated rows from layout:', rows);

    // Add any remaining fields that weren't in the layout config or priority fields
    const remainingFields = fieldsArray.filter(field => !processedFieldNames.has(field.name));
    if (remainingFields.length > 0) {
      //console.log('Remaining fields not in layout:', remainingFields);
      // Group remaining fields in rows of 3
      for (let i = 0; i < remainingFields.length; i += 3) {
        rows.push(remainingFields.slice(i, i + 3));
      }
    }

    return rows;
  };

  // Organize fields into rows based on layout configuration
  const organizedRows = groupFieldsIntoRows(currentFields);

  
  if (!formData) return <FullPageLoader message="Loading form..." />;
  
  if (isLeadCheckStep) {
    return (
      <>
      <style>{pageStyles}</style>
        <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
           <AppHeader 
          isMobile={isMobile}
          formMeta={formMeta}
          userMenuItems={userMenuItems}
          drawerVisible={drawerVisible}
          setDrawerVisible={setDrawerVisible}
          handleLogout={handleLogout}
        />
          <Content style={{ padding: isMobile ? 12 : 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Card style={{ width: isMobile ? '100%' : 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {formMeta.image?.[0]?.public_url && (
                <img
                  src={formMeta.image[0].public_url}
                  alt="Company Logo"
                  style={{
                    display: 'block',
                    margin: '12px auto 24px',
                    maxHeight: 60,
                    maxWidth: '80%',
                    objectFit: 'contain'
                  }}
                />
              )}
              {!formMeta.image?.[0]?.public_url && (
                <h5 style={{ textAlign: 'center', marginTop: 12, marginBottom: 24 }}>Check Lead</h5>
              )}  
              <Form layout="vertical" onFinish={handleLeadCheck} autoComplete="off">
                <Form.Item
                  label="Mobile Number"
                  name="mobileNumber"
                  rules={[{ required: true, message: 'Please enter a mobile number.' }, { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit mobile number.' }]}
                >
                  <Input
                    placeholder="Enter 10-digit mobile number"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    maxLength={10}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting} block style={{ height: 45, background: '#5568d3' }}>
                    {submitting ? 'Checking...' : 'Submit'}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Content>
        </Layout>
      </>
    );
  }

  return (
    <>
    <style>{pageStyles}</style>
    
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader 
        isMobile={isMobile}
        formMeta={formMeta}
        userMenuItems={userMenuItems}
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        handleLogout={handleLogout}
      />

      <Content style={{ 
        padding: isMobile ? 12 : 24,
        background: '#fff',
        overflow: 'auto'
      }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', elevation: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: isMobile ? 12 : 24, borderRadius: 8, background: '#fff'}}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: isMobile ? 12 : 24,
            flexWrap: 'wrap',
            gap: 8
          }}>
            <Button
              type="text"
              className="back-btn"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/companies/${companyId}/site_visit_forms/list`)}
              style={{ paddingLeft: 0 }}
            >
              {!isMobile ? 'Back to Forms' : 'Back'}
            </Button>
            
            {formData?.meta?.history_field_mappings && leadData && (
              <Button
                type="default"
                className="history-btn"
                icon={<HistoryOutlined />}
                onClick={showSiteVisitHistory}
              >
                {!isMobile ? 'Site Visit History' : 'History'}
              </Button>
            )}
          </div>

          {totalSteps  > 1 && (
          <div style={{ marginBottom: isMobile ? 12 : 24 }}>
            <p style={{ color: '#666', margin: 0, fontSize: isMobile ? 12 : 14 }}>
              Step {currentStep + 1} of {totalSteps}
            </p>
          </div>
        )}

          {totalSteps  > 1 && !isMobile && (
            <Steps
              current={currentStep}
              style={{ marginBottom: 30 }}
              items={Array.from({ length: totalSteps }, (_, i) => ({
                title: `Step ${i + 1}`
              }))}
            />
          )}

          <Card style={{ marginBottom: 24}}>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
            >
              {organizedRows.length > 0 ? (
                organizedRows.map((row, rowIdx) => (
                  <React.Fragment key={rowIdx}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      {row.map((field, fieldIdx) => {
                        const isMobile = window.innerWidth < 768;
                        
                        // Calculate column span based on layout configuration
                        let colSpan = 8; // default to 1/3 (8 out of 24)
                        
                        if (isMobile) {
                          colSpan = 24; // Full width on mobile
                        } else if (field._columnCount) {
                          // Use column count from layout config
                          colSpan = Math.floor(24 / field._columnCount);
                        } else if (field.layout) {
                          // Fallback to field's layout property
                          const layout = field.layout;
                          colSpan = layout === 'full' ? 24 : (layout === 'onethird' ? 8 : (layout === 'half' ? 12 : 6));
                        }
                      
                        return (
                          <Col key={field.id || fieldIdx} span={colSpan}>
                            {renderField(field)}
                          </Col>
                        );
                      })}
                    </Row>
                    
                    {/* Render sub_fields for each field in this row */}
                    {row.map((field, fieldIdx) => {
                      const hasSubFields = field.sub_fields && Array.isArray(field.sub_fields) && field.sub_fields.length > 0;
                      
                      if (hasSubFields && shouldShowSubFields(field)) {
                        const relevantSubFields = getRelevantSubFields(field);
                        
                        if (relevantSubFields.length === 0) {
                          return null;
                        }
                        
                        return (
                          <div 
                            key={`sub-${field.id || fieldIdx}`}
                            style={{ 
                              marginLeft: 24, 
                              marginTop: 8,
                              marginBottom: 16,
                              paddingLeft: 16,
                              borderLeft: '3px solid #5568d3'
                            }}
                          >
                            <Row gutter={[16, 16]}>
                              {relevantSubFields.map((subField, subIdx) => {
                                const subLayout = subField.layout || 'onethird';
                                const subColSpan = subLayout === 'full' ? 24 : (subLayout === 'onethird' ? 8 : (subLayout === 'half' ? 12 : (window.innerWidth < 768 ? 24 : 6)));
                                
                                return (
                                  <Col key={subField.id || subIdx} span={subColSpan}>
                                    {renderField(subField, true)}
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </React.Fragment>
                ))
              ) : (
                <p>No fields available</p>
              )}
            </Form>
          </Card>

          {totalSteps > 1 && (
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? 8 : 12, 
            justifyContent: 'space-between',
            marginBottom: 24,
            flexWrap: 'wrap'
          }}>
            
            <Button
              onClick={handlePrev}
              disabled={currentStep === 0}
              style={{ minWidth: isMobile ? 'auto' : 100, flex: isMobile ? 1 : 'auto' }}
            >
              {isMobile ? 'Prev' : 'Previous'}
            </Button>

            <div style={{ display: 'flex', gap: isMobile ? 8 : 12, flex: isMobile ? 1 : 'auto' }}>
              {currentStep === totalSteps - 1 ? (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{ 
                    background: '#5568d3', 
                    minWidth: isMobile ? 'auto' : 150,
                    flex: isMobile ? 1 : 'auto'
                  }}
                >
                  {submitting ? 'Submitting...' : (isMobile ? 'Submit' : 'Submit Form')}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleNext}
                  style={{ minWidth: isMobile ? 'auto' : 100, flex: isMobile ? 1 : 'auto' }}
                >
                  {isMobile ? 'Next' : 'Next'}
                </Button>
              )}
            </div>
          </div>
          )
          }
            {
            totalSteps === 1 && (
              <div style={{ 
                marginBottom: 24,
                textAlign: isMobile ? 'center' : '-webkit-center'
              }}>
               <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{ 
                    background: '#5568d3', 
                    minWidth: isMobile ? 'auto' : 150,
                    flex: isMobile ? 1 : '',
                    alignItems: 'center'
                  }}
                >
                  {submitting ? 'Submitting...' : (isMobile ? 'Submit' : 'Submit')}
                </Button>
                </div>
            )   
            }

         
        </div>
      </Content>

      {/* Site Visit History Modal */}
      <Modal
        title="Site Visit History"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Close
          </Button>
        ]}
        width={isMobile ? '95%' : 800}
        style={{ top: isMobile ? 20 : 50 }}
      >
        {historyData.length > 0 ? (
          <Table
            dataSource={historyData}
            pagination={false}
            scroll={{ x: isMobile ? 600 : undefined }}
            size={isMobile ? 'small' : 'middle'}
            columns={[
              {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                width: isMobile ? 100 : 120,
                render: (text) => text || '-'
              },
              {
                title: 'Project Unit',
                dataIndex: 'projectUnit',
                key: 'projectUnit',
                width: isMobile ? 120 : 150,
                render: (text) => text || '-'
              },
              {
                title: 'Budget',
                dataIndex: 'budget',
                key: 'budget',
                width: isMobile ? 100 : 120,
                render: (text) => text || '-'
              },
              {
                title: 'Channel Partner',
                dataIndex: 'channelPartner',
                key: 'channelPartner',
                width: isMobile ? 120 : 150,
                render: (text) => text || '-'
              }
            ]}
          />
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#666'
          }}>
            <HistoryOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d9d9d9' }} />
            <p>No site visit history available</p>
          </div>
        )}
      </Modal>
    </Layout>
    </>
  );
};

export default FormWizard;
